<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.PRODUCTS.TITLE") }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab">
          <v-btn
            v-if="hasPermissions('stock-products-create')"
            class="mr-4 success"
            :disabled="isProcessing"
            @click="handleCreateClick"
          >
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-products-table"
                height="500"
                name="stock-products-table"
                :headers="headers"
                :blink-row-item-key="blinkRowItemKey"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="100"
                :loading="isProcessing"
                :filters="tableFilters"
                dense
                fixed-header
                multi-sort
                filterable
                @filters-change="handleFiltersChange"
              >
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <span
                    :key="index"
                    v-if="header.value === 'options'"
                    class="d-flex align-center justify-center flex-nowrap"
                  >
                    <v-btn
                      v-if="hasPermissions('stock-products-edit')"
                      icon
                      color="primary"
                      @click="handleEditClick(item.item.id)"
                    >
                      <v-icon dark>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="hasPermissions('stock-products-delete')"
                      icon
                      color="error"
                      @click="handleDeleteClick(item.item)"
                    >
                      <v-icon dark>mdi-delete</v-icon>
                    </v-btn>
                  </span>
                  <template v-else-if="header.value === 'display_name'">
                    {{ item.item.display_name || "-" }}
                  </template>
                  <template v-else-if="header.value === 'stock_product_department.name'">
                    {{ item.item.stock_product_department ? item.item.stock_product_department.label : "-" }}
                  </template>
                  <template v-else-if="header.value === 'stock_product_category.name'">
                    {{ item.item.stock_product_category ? item.item.stock_product_category.label : "-" }}
                  </template>
                  <template v-else-if="header.value === 'temperature.name'">
                    {{ item.item.temperature ? item.item.temperature.label : "-" }}
                  </template>
                  <template v-else-if="header.value === 'packaging_levels'">
                    {{ formatPackagingLevels(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'origin_country.name'">
                    {{ item.item.origin_country ? (item.item.origin_country.name || item.item.origin_country.label) : "-" }}
                  </template>
                  <template v-else-if="header.value === 'stock_tags'">
                    <v-chip
                      v-for="tag in item.item.stock_tags"
                      :key="tag.id"
                      x-small
                      class="ma-1"
                      color="teal"
                      dark
                    >
                      {{ tag.label }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'primary_unit.code'">
                    {{ item.item.primary_unit ? item.item.primary_unit.code : "kg" }}
                  </template>
                  <template v-else-if="header.value === 'fixed_weight'">
                    <v-chip x-small :color="item.value ? 'primary' : 'grey'" dark>
                      {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : "-" }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'is_active'">
                    <v-chip x-small :color="item.value ? 'success' : 'error'" dark>
                      {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                    </v-chip>
                  </template>
                  <template v-else>
                    {{ item.value || "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <TPConfirm
      v-model="showDeleteConfirm"
      :content="$t('GENERAL.DELETE_CONFIRM')"
      @confirm="deleteProduct"
    ></TPConfirm>
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import userStateService from "@/common/userState.service";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  DELETE_STOCK_PRODUCT,
  GET_STOCK_PRODUCTS,
  GET_STOCK_TEMPERATURE_OPTIONS,
} from "@/store/stock/stockProducts.module";
import { GET_STOCK_PRODUCT_CATEGORY_OPTIONS } from "@/store/stock/stockProductCategories.module";
import { GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS } from "@/store/stock/stockProductDepartments.module";

export default {
  name: "StockListProducts",
  components: {
    Table,
    TPConfirm,
  },
  data() {
    return {
      tableName: "stock-products-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      showDeleteConfirm: false,
      selectedProduct: null,
      tableItemsMethod: GET_STOCK_PRODUCTS,
      tableFilters: [],
      allCategories: [],
      allDepartments: [],
      activeDepartmentIds: [],
      headers: [
        { text: "#", value: "id", align: "center", width: "5%" },
        { text: this.$t("STOCK.PRODUCTS.CODE"), value: "code", align: "center", width: "8%" },
        { text: this.$t("STOCK.PRODUCTS.DEPARTMENT"), value: "stock_product_department.name", sortable: false, align: "center", width: "8%" },
        { text: this.$t("STOCK.PRODUCTS.CATEGORY"), value: "stock_product_category.name", sortable: false, align: "center", width: "8%" },
        { text: this.$t("STOCK.PRODUCTS.DISPLAY_NAME"), value: "display_name", align: "center", width: "14%" },
        { text: this.$t("STOCK.PRODUCTS.TEMPERATURE"), value: "temperature.name", sortable: false, align: "center", width: "7%" },
        { text: this.$t("STOCK.PRODUCTS.PACKAGING_LEVELS"), value: "packaging_levels", sortable: false, align: "center", width: "10%" },
        { text: this.$t("STOCK.PRODUCTS.ORIGIN_COUNTRY"), value: "origin_country.name", sortable: false, align: "center", width: "8%" },
        { text: this.$t("STOCK.PRODUCTS.TAGS"), value: "stock_tags", sortable: false, align: "center", width: "10%" },
        { text: this.$t("STOCK.PRODUCTS.UNIT"), value: "primary_unit.code", sortable: false, align: "center", width: "5%" },
        { text: this.$t("STOCK.PRODUCTS.FIXED_WEIGHT"), value: "fixed_weight", align: "center", width: "6%" },
        { text: this.$t("GENERAL.LABELS.STATUS"), value: "is_active", align: "center", width: "6%" },
        { text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"), value: "options", sortable: false, align: "center", width: "8%" },
      ],
    };
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    this.loadFilters().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    formatPackagingLevels(product) {
      const levels = product?.packaging_levels || [];
      if (!levels.length) {
        return "-";
      }

      return levels
        .map((level) => {
          const name = level.packaging?.label || level.packaging?.name || level.packaging_name;
          if (!name || level.weight_kg == null) {
            return null;
          }
          return `${name} ${level.weight_kg}kg`;
        })
        .filter(Boolean)
        .join(" / ") || "-";
    },
    loadFilters() {
      return Promise.all([
        this.$store.dispatch(GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_TEMPERATURE_OPTIONS),
      ]).then(([departmentsResponse, categoriesResponse, temperaturesResponse]) => {
        this.allDepartments = departmentsResponse.data || [];
        this.allCategories = categoriesResponse.data || [];
        this.buildTableFilters(
          this.allDepartments,
          this.getFilteredCategories([]),
          temperaturesResponse.data || []
        );
      });
    },
    getFilteredCategories(departmentIds) {
      if (!departmentIds.length) {
        return this.allCategories;
      }

      return this.allCategories.filter((category) =>
        departmentIds.includes(category.stock_product_department_id)
      );
    },
    buildTableFilters(departments, categories, temperatures) {
      this.tableFilters = [
        {
          key: "stock_product_department_id",
          label: this.$t("STOCK.PRODUCTS.DEPARTMENT"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: departments,
        },
        {
          key: "stock_product_category_id",
          label: this.$t("STOCK.PRODUCTS.CATEGORY"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: categories,
        },
        {
          key: "temperature_id",
          label: this.$t("STOCK.PRODUCTS.TEMPERATURE"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: temperatures,
        },
      ];
    },
    pruneCategoryFilterSelection(filters, categories) {
      const selectedCategoryIds = filters.stock_product_category_id || [];
      if (!selectedCategoryIds.length) {
        return false;
      }

      const allowedIds = new Set(categories.map((category) => category.id));
      const pruned = selectedCategoryIds.filter((id) => allowedIds.has(id));
      if (JSON.stringify(pruned) === JSON.stringify(selectedCategoryIds)) {
        return false;
      }

      filters.stock_product_category_id = pruned;
      const table = this.$refs["stock-products-table"];
      if (table) {
        table.serverFilters = { ...filters };
      }
      return true;
    },
    handleFiltersChange(filters) {
      const departmentIds = filters.stock_product_department_id || [];
      const departmentChanged =
        JSON.stringify(departmentIds) !== JSON.stringify(this.activeDepartmentIds);

      if (!departmentChanged) {
        return;
      }

      this.activeDepartmentIds = [...departmentIds];
      const filteredCategories = this.getFilteredCategories(departmentIds);
      const pruned = this.pruneCategoryFilterSelection(filters, filteredCategories);
      this.buildTableFilters(
        this.allDepartments,
        filteredCategories,
        this.tableFilters.find((filter) => filter.key === "temperature_id")?.items || []
      );
      if (pruned) {
        this.reloadTable();
      }
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      return error?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    reloadTable() {
      this.$nextTick(() => this.$refs["stock-products-table"]?.fetchServerData());
    },
    handleCreateClick() {
      this.$router.push({ name: "stock-products-create" });
    },
    handleEditClick(id) {
      userStateService.setState(`table-state.${this.tableName}.blinkRowItemKey`, id);
      this.$router.push({ name: "stock-products-edit", params: { id } });
    },
    handleDeleteClick(product) {
      this.selectedProduct = product;
      this.showDeleteConfirm = true;
    },
    deleteProduct() {
      if (!this.selectedProduct) {
        return;
      }
      this.isProcessing = true;
      this.$store
        .dispatch(DELETE_STOCK_PRODUCT, this.selectedProduct.id)
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
          this.reloadTable();
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.selectedProduct = null;
        });
    },
  },
};
</script>
